import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

const sections = [
  {
    id: '01',
    title: 'Acceptance of Terms',
    content: `By accessing or using the Mino platform ("Mino", "we", "us", or "our"), including our website, mobile application, and all related services, you ("User", "you") agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms in their entirety, you must immediately cease using Mino.

These Terms constitute a legally binding agreement between you and Mino. We reserve the right to modify these Terms at any time. Continued use of the platform after any such changes constitutes your acceptance of the new Terms. It is your responsibility to review these Terms periodically.

Your use of Mino is also governed by our Privacy Policy and Responsible Trading Policy, both of which are incorporated into these Terms by reference.`,
  },
  {
    id: '02',
    title: 'Eligibility & Account Registration',
    content: `You must be at least 18 years of age to use Mino. By using our platform you represent and warrant that you meet this requirement. Mino reserves the right to request proof of age at any time and to suspend any account where we have reason to believe the user is underage.

You must be a resident of the Republic of Kenya or otherwise authorized to use forecasting and prediction market services in your jurisdiction. It is your sole responsibility to ensure compliance with all applicable laws in your jurisdiction.

When creating an account you agree to provide accurate, complete, and current information; maintain the security of your password and account credentials; notify us immediately of any unauthorized use of your account; accept responsibility for all activities that occur under your account; and use only one account — multiple accounts are strictly prohibited.

Mino reserves the right to refuse registration, suspend, or permanently terminate any account at our sole discretion, including cases of suspected fraud, abuse, or violation of these Terms. After 5 consecutive failed login attempts, your account will be locked for 15 minutes as a security measure.`,
  },
  {
    id: '03',
    title: 'Prediction Markets & How They Work',
    content: `Mino operates a parimutuel prediction market platform where users take positions on the outcomes of real-world events. This is a forecasting platform — users apply research, analysis, and informed judgment to predict verifiable outcomes. Mino is not a gambling or betting platform.

In a parimutuel system, all funds allocated to a market are pooled together. Users who correctly forecast the outcome receive a proportional share of the total pool, minus Mino's platform fees, based on the amount they allocated relative to other correct forecasters.

Market outcomes are determined solely at Mino's discretion based on verifiable real-world data. Mino's resolution decisions are final, binding, and non-appealable. By participating in any market you acknowledge and accept this.

Markets have defined closing times after which no new positions may be opened. Markets are resolved after the real-world event has concluded and the outcome has been verified by Mino's team against credible public sources.

Mino reserves the right to cancel or void any market at any time before resolution without prior notice; modify the closing time of any market; reject any position for any reason; and suspend trading on any market at any time. In the event a market is cancelled or voided, all allocated funds will be returned to users' Mino balances.`,
  },
  {
    id: '04',
    title: 'Platform Fees & Payouts',
    content: `Mino charges the following platform fees:

Entry Fee (2%): A 2% fee is deducted at the time of opening a position. Only 98% of your allocated amount enters the market pool. This fee is non-refundable.

Resolution Fee (2%): Upon market resolution, Mino deducts 2% from the total market pool before distributing payouts to winners.

Early Exit Fee (1% + tiered exit penalty): If you choose to exit a position before market resolution, a 1% platform fee plus a tiered exit penalty is applied to your exit value. The exit penalty is 20% if more than 30 days remain on the market, or 30% if fewer than 30 days remain.

Withdrawal Fee (2%): A 2% fee applies to withdrawals of amounts above KES 110. Withdrawals of KES 110 or below are processed free of charge.

Payout formula: Your payout = (Your shares / Total winning shares) × (Total market pool × 0.98) minus 20% withholding tax on net gains.


The 20% withholding tax is applied only on net gains at the time of resolution. All fees are automatically deducted before amounts are credited to your account. All amounts on the Mino platform are denominated in Kenyan Shillings (KES).`,
  },
  {
    id: '05',
    title: 'Deposits & Withdrawals',
    content: `Mino facilitates deposits and withdrawals via M-Pesa mobile money. By using our payment services you agree to the terms and conditions of M-Pesa and Safaricom.

Deposits: Minimum deposit is KES 30. Maximum deposit is KES 300,000. Deposits are subject to M-Pesa availability and processing times. Mino is not responsible for delays caused by M-Pesa or Safaricom.

Withdrawals: Minimum withdrawal is KES 100. A 2% withdrawal fee applies to amounts above KES 110. Withdrawals of KES 110 or below are free of charge. A 20% withholding tax is deducted from your net gains upon winning a market, in compliance with Kenyan law. This is remitted to the Kenya Revenue Authority (KRA) on your behalf.

Mino reserves the right to delay or refuse any withdrawal pending identity or fraud verification; freeze account funds if fraud or abuse is suspected; withhold funds deposited or earned through fraudulent means; and set minimum and maximum withdrawal limits at any time.

Unclaimed funds in accounts inactive for 12 consecutive months may be forfeited after reasonable notice.`,
  },
  {
    id: '06',
    title: 'Responsible Trading',
    content: `Mino is committed to promoting responsible use of our forecasting platform. We recognize that prediction markets carry financial risk and encourage all users to engage responsibly and within their means.

Mino provides built-in responsible trading tools accessible directly from your profile: daily, weekly, and monthly deposit limits; self-exclusion for 24 hours, 7 days, 30 days, or permanently; and a cooling period requiring 24 hours before deposit limit increases take effect.

We strongly recommend that you never allocate funds intended for essential expenses such as rent, food, or bills; never chase losses by opening increasingly large positions; set personal limits and stick to them; take regular breaks from the platform; and seek help if your use of the platform is becoming compulsive.

Users who self-exclude must not attempt to circumvent these measures by creating new accounts. This is a violation of these Terms and will result in permanent ban and fund forfeiture.`,
  },
  {
    id: '07',
    title: 'Prohibited Conduct',
    content: `The following conduct is strictly prohibited and may result in immediate account suspension, permanent ban, and forfeiture of all funds:

Using automated bots, scripts, or software to open positions or manipulate markets; collusion with other users to manipulate market outcomes or probabilities; creating multiple accounts to circumvent limits, restrictions, or bans; providing false information during registration or verification; using stolen, fraudulent, or unauthorized payment methods; attempting to exploit bugs, glitches, or technical errors; market manipulation; using privileged or insider knowledge to gain unfair advantage; using the platform for money laundering or any illegal financial activity; accessing the platform from jurisdictions where prediction markets are prohibited; and attempting to circumvent self-exclusion by creating new accounts.

Mino's determination of prohibited conduct is final and binding.`,
  },
  {
    id: '08',
    title: 'Taxes & Regulatory Compliance',
    content: `Mino operates in compliance with Kenyan tax and financial regulations. A 20% withholding tax is applied to net gains at the time of market resolution, as required under Kenyan law. Net gains are calculated as your gross payout minus your original stake (after the 2% entry fee). This tax is deducted automatically before your winnings are credited to your Mino balance and remitted to the Kenya Revenue Authority (KRA) on your behalf.

Users are solely responsible for any additional tax obligations arising from their use of Mino, including reporting any returns as required by KRA.

Mino complies with the Financial Reporting Centre Act (FRCA) and may report suspicious transactions to the Financial Reporting Centre (FRC) as required by law. Mino may request identity verification and source of funds documentation at any time to comply with Anti-Money Laundering (AML) regulations.

Mino operates as a forecasting platform and is not classified as a betting or gambling operator under the Kenyan Betting, Lotteries and Gaming Act.`,
  },
  {
    id: '09',
    title: 'Intellectual Property',
    content: `All content on the Mino platform, including text, graphics, logos, icons, images, data compilations, and software, is the property of Mino or its content suppliers and is protected by Kenyan and international intellectual property laws.

You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Mino platform for personal, non-commercial purposes only. This license does not permit copying, distributing, modifying, scraping, or reverse engineering any part of the platform.`,
  },
  {
    id: '10',
    title: 'Limitation of Liability',
    content: `To the maximum extent permitted by applicable law, Mino, its directors, employees, partners, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform.

Mino's total liability to you for any claim shall not exceed the amount of your current Mino account balance at the time of the claim.

Prediction markets involve financial risk. You may lose all funds allocated to a position. Never allocate more than you can afford to lose. Mino does not guarantee any specific financial outcome or return on your forecasts. Past market performance is not indicative of future results.

Mino is not liable for any losses arising from M-Pesa outages, network failures, or third-party service disruptions.`,
  },
  {
    id: '11',
    title: 'Dispute Resolution & Governing Law',
    content: `These Terms shall be governed by and construed in accordance with the laws of the Republic of Kenya.

In the event of any dispute, you agree to first attempt informal resolution by contacting Mino at support@minomarkets.app. Mino will make reasonable efforts to resolve disputes within 30 days.

If informal resolution fails, any dispute shall be resolved by binding arbitration in Nairobi, Kenya, in accordance with the Arbitration Act of Kenya. The arbitrator's decision shall be final and binding.

You waive any right to participate in a class action lawsuit against Mino.`,
  },
  {
    id: '12',
    title: 'General Provisions',
    content: `Beta Platform Notice: Mino is currently operating in an early access beta phase while formal regulatory approvals are being sought from the Capital Markets Authority (CMA) of Kenya. The platform is fully functional and all transactions are processed normally. Your funds are held securely.

Entire Agreement: These Terms, together with our Privacy Policy and Responsible Trading Policy, constitute the entire agreement between you and Mino.

Severability: If any provision is found unenforceable, the remaining provisions continue in full force.

Force Majeure: Mino shall not be liable for failures due to circumstances beyond our reasonable control, including government actions, network failures, or M-Pesa outages.

Contact: For questions about these Terms, contact us at support@minomarkets.app

Last Updated: April 2026`,
  },
];

const KEY_POINTS = [
  'Must be 18 years of age or older to use Mino',
  '2% entry fee · 2% resolution fee · 2% withdrawal fee (above KES 110)',
  "Mino's market resolution decisions are final and binding",
  'No bots, multi-accounts, or collusion permitted',
  '20% withholding tax applied to net gains at resolution',
  'Forecasting platform — not a gambling or betting operator',
  'Platform currently in beta — CMA approval in progress',
  'Secondary market for position trading coming as platform grows',
];

export default function Terms() {
  const [activeSection, setActiveSection] = useState(null);
  const navigate = useNavigate();
  const [heroRef, heroVisible] = useInView(0.05);

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>Terms & Conditions — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="Terms and Conditions governing your use of Mino Markets." />
      </Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.12); }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        .mino-aurora {
          position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
        }
        .mino-aurora::before {
          content: '';
          position: absolute;
          width: 140%; height: 55%;
          top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%,
            transparent 0deg,
            rgba(55,114,255,0.09) 40deg,
            rgba(22,199,132,0.07) 80deg,
            rgba(124,58,237,0.08) 120deg,
            transparent 160deg
          );
          filter: blur(60px);
          animation: auroraShift 20s ease-in-out infinite alternate;
          transform-origin: center top;
        }
        .mino-aurora::after {
          content: '';
          position: absolute;
          width: 80%; height: 40%;
          bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%,
            transparent 0deg,
            rgba(22,199,132,0.06) 50deg,
            rgba(55,114,255,0.05) 100deg,
            transparent 150deg
          );
          filter: blur(70px);
          animation: auroraShift2 26s ease-in-out infinite alternate;
          transform-origin: center bottom;
        }
        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }
        .mino-beam {
          position: fixed; pointer-events: none; z-index: 0;
          width: 1px; top: 0; bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
          animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
        }
        @keyframes auroraShift {
          0%   { transform: rotate(-4deg) scaleX(1);    opacity: 1; }
          50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
          100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
        }
        @keyframes auroraShift2 {
          0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
          50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
          100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
        }
        @keyframes beamDrift {
          0%   { left: var(--from); opacity: 0.5; }
          100% { left: var(--to);   opacity: 1; }
        }

        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; }
        .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

        .keypoint-item { transition: border-color 0.2s ease, background 0.2s ease; }
        .keypoint-item:hover { border-color: rgba(255,255,255,0.14) !important; background: rgba(255,255,255,0.04) !important; }

        .section-item { transition: border-color 0.25s ease, background 0.25s ease; }
        .section-item:hover { border-color: rgba(255,255,255,0.12) !important; }
      `}</style>

      <div className="mino-aurora" />
      <div className="mino-dot-grid" />
      <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
      <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
      <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {[['Privacy', '/privacy'], ['Responsible Trading', '/responsible-gambling']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
        <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 20px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 400, cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#e8e6e0'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
          Back
        </button>
      </nav>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>

        {/* Hero */}
        <div ref={heroRef} style={{ paddingTop: 120, paddingBottom: 100, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)' }}>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Legal · 18+ Only · Updated April 2026</p>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(48px,7vw,88px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-3px', color: '#e8e6e0', marginBottom: 36, maxWidth: 720 }}>
            Terms &<br />Conditions.
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.36)', lineHeight: 1.8, maxWidth: 480, fontWeight: 300 }}>
            These Terms govern your use of the Mino prediction market platform. By using Mino you agree to be legally bound by these Terms.
          </p>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Beta notice */}
        <div style={{ padding: '60px 0 0' }}>
          <FadeUp>
            <div style={{ padding: '20px 28px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, background: 'rgba(255,255,255,0.02)', marginBottom: 48 }}>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 10 }}>Beta Notice</p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.34)', lineHeight: 1.8, fontWeight: 300 }}>
                Mino is currently operating in an early access beta phase while formal regulatory approvals are being sought from the Capital Markets Authority (CMA) of Kenya. The platform is fully functional, all transactions are processed normally, and your funds are held securely.
              </p>
            </div>
          </FadeUp>
        </div>

        {/* Key Points */}
        <div style={{ paddingBottom: 100 }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 32 }}>Key Points</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {KEY_POINTS.map((pt, i) => (
                <div key={i} className="keypoint-item" style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '16px 18px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.18)', flexShrink: 0, marginTop: 3, fontWeight: 500, letterSpacing: '0.06em' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, fontWeight: 300 }}>{pt}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Secondary Market callout */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)', marginBottom: 20 }}>Platform Roadmap</p>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(24px,3vw,36px)', fontWeight: 700, color: '#e8e6e0', letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 24 }}>
                  Your positions will become assets.
                </h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, marginBottom: 18, fontWeight: 300 }}>
                  The current exit model — with its tiered penalties — is a temporary structure built for the platform's early stage.
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>
                  As Mino grows, we're developing a live secondary market where users can sell positions peer-to-peer before resolution, at real-time market prices. Lock in profits, cut losses, on your terms.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[['Peer-to-peer selling', 'Sell positions to other users before resolution'], ['Lock in profits early', 'Exit when you\'re ahead, not when the market resolves'], ['Live position pricing', 'Values update based on real-time market movement'], ['No penalties on secondary trades', 'Replacing the current tiered exit penalty model']].map(([label, desc], i, a) => (
                  <div key={label} style={{ padding: '20px 0', borderBottom: i < a.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 600, color: '#e8e6e0', marginBottom: 6, letterSpacing: '-0.1px' }}>{label}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.6, fontWeight: 300 }}>{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Accordion sections */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 32 }}>Sections</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {sections.map((section) => {
                const isOpen = activeSection === section.id;
                return (
                  <div key={section.id} className="section-item" style={{ background: isOpen ? 'rgba(255,255,255,0.03)' : 'transparent', border: `1px solid ${isOpen ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 12, overflow: 'hidden' }}>
                    <button onClick={() => setActiveSection(isOpen ? null : section.id)} style={{ width: '100%', background: 'transparent', border: 'none', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', textAlign: 'left', gap: 16, fontFamily: 'inherit' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.08em', flexShrink: 0 }}>{section.id}</span>
                        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 500, color: isOpen ? '#e8e6e0' : 'rgba(255,255,255,0.45)', transition: 'color 0.2s' }}>{section.title}</span>
                      </div>
                      <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 20, transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)', transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', flexShrink: 0, fontWeight: 300, lineHeight: 1 }}>+</span>
                    </button>
                    <div style={{ maxHeight: isOpen ? 1200 : 0, overflow: 'hidden', transition: 'max-height 0.45s cubic-bezier(0.16,1,0.3,1)' }}>
                      <div style={{ padding: '0 28px 32px' }}>
                        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 22 }} />
                        {section.content.split('\n\n').map((para, j) => (
                          <p key={j} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.34)', lineHeight: 1.88, marginBottom: 16, whiteSpace: 'pre-line', fontWeight: 300 }}>{para}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Footer CTA */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { label: 'Legal Questions?', sub: 'For questions about these Terms, reach our legal team.', link: 'support@minomarkets.app' },
                { label: 'Need Support?', sub: 'For account or platform support, contact us directly.', link: 'support@minomarkets.app' },
              ].map((card, i) => (
                <a key={card.label} href={`mailto:${card.link}`} style={{ border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: '36px 32px', background: 'rgba(255,255,255,0.025)', transition: 'all 0.2s', textDecoration: 'none', display: 'block' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}>
                  <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, color: '#e8e6e0', marginBottom: 8, fontSize: 16, letterSpacing: '-0.3px' }}>{card.label}</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.28)', fontSize: 13, marginBottom: 20, lineHeight: 1.6, fontWeight: 300 }}>{card.sub}</p>
                  <span style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.45)', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {card.link}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </span>
                </a>
              ))}
            </div>
          </FadeUp>
        </div>

      </div>

      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>© 2026 Mino · Governed by the laws of the Republic of Kenya</span>
        <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
          Back to markets
        </span>
      </div>
    </div>
  );
}